import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { environment } from 'src/environments/environment';

/**
 * Single entry point to the shop's Firebase project (environment.firebase; empty, sign-in with Google is off).
 *
 * Two things shape this file.
 *
 * SSR. The app renders on a Node process (ClientApp/dist-server/main.js), and most
 * Firebase products reach for window, document or IndexedDB the moment they load.
 * `firebase/app` itself is safe on the server, so it is imported normally; everything
 * else is loaded with a dynamic import inside a browser-only branch, which keeps it out
 * of the server's execution path entirely. A product that throws during a render does
 * not just lose that feature - it takes the whole page down.
 *
 * One app instance. initializeApp() throws if it is called twice with the same name,
 * and the SPA re-enters this service on every lazy module, so the app is looked up
 * before it is created.
 *
 * ADDING ANOTHER PRODUCT - messaging, firestore, storage - follows the auth shape
 * below: `this.getApp()`, then a dynamic import of the product entry point inside a
 * browser-only branch. Do not add a top-level import of anything but `firebase/app`.
 *
 * Analytics is deliberately NOT here. Firebase Analytics would not start without
 * IndexedDB and stalled behind a Firebase Installations call that ad blockers refuse,
 * silently dropping those visitors; Google Analytics is loaded directly by
 * AnalyticsService instead.
 */
/**
 * What a sign-in attempt produced.
 *
 * `redirecting` is not a failure: the browser is on its way to Google and the answer
 * arrives on the next page load, so the caller should stop and let it navigate.
 */
export interface GoogleSignInAttempt {
    idToken?: string;
    cancelled?: boolean;
    redirecting?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class FirebaseService {

    /// Marks a redirect sign-in as in flight. sessionStorage, not localStorage: it
    /// belongs to this tab's journey to Google and back, and must not leak into others.
    private static readonly RedirectFlag = 'izz_google_redirect';

    private readonly isBrowser: boolean;
    private app: FirebaseApp = null;

    constructor(@Inject(PLATFORM_ID) platformId: Object) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    /// The FirebaseApp, created on first use. Safe to call on the server: the core app
    /// object is just configuration, and other products decide for themselves whether
    /// they can run.
    getApp(): FirebaseApp {
        if (this.app) { return this.app; }

        const config = (environment as any).firebase;
        if (!config || !config.apiKey) {
            return null;
        }

        // getApps() rather than a try/catch around initializeApp: a duplicate-app error
        // is indistinguishable from a real config error once it has been caught.
        this.app = getApps().length ? getApp() : initializeApp(config);
        return this.app;
    }

    /**
     * Opens Google's sign-in popup and resolves with the Firebase ID token, or null if
     * the person closed it. The token is the only thing worth sending to the server -
     * it is signed by Google, so the server can prove who they are from it alone.
     *
     * Rejects only on a real failure, so a cancelled popup is not reported as an error.
     *
     * firebase/auth is imported dynamically: it reaches for browser APIs at load time
     * and would break the Node render if it were a top-level import.
     */
    async signInWithGoogle(): Promise<GoogleSignInAttempt> {
        if (!this.isBrowser) { return { cancelled: true }; }

        const app = this.getApp();
        if (!app) { throw new Error('Firebase is not configured.'); }

        const auth = await import('firebase/auth');
        const instance = auth.getAuth(app);
        const provider = new auth.GoogleAuthProvider();

        // Always ask which account to use. Without this Google silently reuses the one
        // already signed in on the device, which is wrong on a shared computer and
        // confusing for anyone with more than one address.
        provider.setCustomParameters({ prompt: 'select_account' });

        try {
            const credential = await auth.signInWithPopup(instance, provider);
            return credential && credential.user
                ? { idToken: await credential.user.getIdToken() }
                : { cancelled: true };
        } catch (error) {
            const code = error && (error as any).code;

            // Closing the popup, or clicking the button twice, is not an error worth
            // showing anyone.
            if (code === 'auth/popup-closed-by-user'
                || code === 'auth/cancelled-popup-request'
                || code === 'auth/user-cancelled') {
                return { cancelled: true };
            }

            // A popup is not always available. Facebook's and Instagram's in-app
            // browsers block window.open outright, as do webviews and anyone with a
            // popup blocker - and a good share of shoppers arrive through exactly those.
            // Sending them to Google in the top-level window works everywhere; the
            // answer comes back on the next page load, which is what
            // completeGoogleRedirect picks up.
            if (code === 'auth/popup-blocked'
                || code === 'auth/operation-not-supported-in-this-environment') {

                // Marks that a redirect is in flight, so the next load knows to look for
                // a result. Without this the auth module would have to be loaded on
                // EVERY page just to ask, which would undo the lazy chunk.
                try { sessionStorage.setItem(FirebaseService.RedirectFlag, '1'); } catch { }

                await auth.signInWithRedirect(instance, provider);
                return { redirecting: true };
            }

            throw error;
        }
    }

    /// True when a redirect sign-in was started and has not been collected yet. Cheap:
    /// it reads one sessionStorage key and loads nothing.
    get hasPendingRedirect(): boolean {
        if (!this.isBrowser) { return false; }
        try { return sessionStorage.getItem(FirebaseService.RedirectFlag) === '1'; }
        catch { return false; }
    }

    /**
     * Collects the result of a redirect sign-in, or null if there is nothing to collect.
     * Clears the pending flag either way, so a failed attempt cannot leave the app
     * checking on every load forever.
     */
    async completeGoogleRedirect(): Promise<string> {
        if (!this.hasPendingRedirect) { return null; }

        try { sessionStorage.removeItem(FirebaseService.RedirectFlag); } catch { }

        const app = this.getApp();
        if (!app) { return null; }

        try {
            const auth = await import('firebase/auth');
            const result = await auth.getRedirectResult(auth.getAuth(app));
            return result && result.user ? await result.user.getIdToken() : null;
        } catch {
            // The person may simply have declined at Google's screen.
            return null;
        }
    }

    /// Ends the Firebase session. The app's own JWT is separate and is cleared by
    /// UserService - this only stops Google from silently signing the person back in.
    async signOutGoogle(): Promise<void> {
        if (!this.isBrowser || !this.app) { return; }

        try {
            const auth = await import('firebase/auth');
            await auth.signOut(auth.getAuth(this.app));
        } catch {
            // Nothing depends on this succeeding.
        }
    }
}
